import { Button, Checkbox, FormGroup, HTMLSelect } from '@blueprintjs/core'
import * as React from 'react'
import { CSSProperties } from 'react'
import { useBlockLayout, useResizeColumns, useSortBy, useTable } from 'react-table'
import Spinner from '../../../components/Spinner'
import { getSort } from '../../../utils'

const paginationButtonStyle: CSSProperties = {
  width: 100,
  textAlign: 'center'
}

const SELECTION_COLUMN_WIDTH = 40

const SELECTION_COLUMN_STYLE: CSSProperties = {
  width: SELECTION_COLUMN_WIDTH,
  minWidth: SELECTION_COLUMN_WIDTH,
  maxWidth: SELECTION_COLUMN_WIDTH
}

const style = {
  pagination: {
    button: paginationButtonStyle,
    pageSize: {
      marginLeft: 20,
      marginRight: 20,
      marginBottom: 0
    },
    div: {
      marginTop: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }
}

const CursorPaginatedTable = ({
  meta,
  onMetaChange,
  data,
  idKey,
  columns,
  loading,
  isSelected,
  toggleSelection,
  toggleAll,
  selectAll,
  ...rest
}) => {
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>({})
  const [headerHeight, setHeaderHeight] = React.useState(0)
  const headerRef = React.useRef<HTMLTableSectionElement | null>(null)
  const measurementHeaderRefs = React.useRef<Record<string, HTMLTableCellElement | null>>({})

  const visibleColumns = React.useMemo(() => columns.filter(c => c.show !== false), [columns])
  const metaSort = meta?.sort
  const tableData = React.useMemo(
    () =>
      data.map(entry => ({
        _id: entry[idKey || 'id'],
        ...entry
      })),
    [data, idKey]
  )

  const baseColumns = React.useMemo(
    () =>
      visibleColumns.map(col => ({
        ...col,
        disableSortBy: col.sortable === false
      })),
    [visibleColumns]
  )

  const columnIds = React.useMemo(() => baseColumns.map(column => column.id), [baseColumns])

  const hasInitialWidths = React.useMemo(
    () => columnIds.length > 0 && columnIds.every(id => columnWidths[id] != null),
    [columnIds, columnWidths]
  )

  const tableColumns = React.useMemo(
    () =>
      baseColumns.map(col => ({
        ...col,
        width: columnWidths[col.id] ?? col.width
      })),
    [baseColumns, columnWidths]
  )

  const initialSortBy = React.useMemo(() => {
    const sort = getSort(metaSort)
    if (!sort || !sort.length) return []
    return sort.map(s => ({ id: s.id, desc: s.desc }))
  }, [metaSort])

  const tableConfig = {
    data: tableData,
    manualSortBy: true,
    autoResetSortBy: false,
    initialState: {
      sortBy: initialSortBy
    } as any,
    getRowId: (row: any) => String(row._id),
    ...rest
  }

  const measurementInstance = useTable(
    {
      ...tableConfig,
      columns: baseColumns
    },
    useSortBy
  ) as any

  const resizableInstance = useTable(
    {
      ...tableConfig,
      columns: tableColumns
    },
    useBlockLayout,
    useResizeColumns,
    useSortBy
  ) as any

  const tableInstance = hasInitialWidths ? resizableInstance : measurementInstance
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state } = tableInstance

  const { sortBy, columnResizing } = state as any
  const sortValue = sortBy.length ? `${sortBy[0].id}.${sortBy[0].desc ? 'desc' : 'asc'}` : undefined

  React.useEffect(() => {
    if (!hasInitialWidths) return
    if (columnResizing?.isResizingColumn) return

    const newWidths = columnResizing?.columnWidths
    if (newWidths && Object.keys(newWidths).length > 0) {
      setColumnWidths(prev => ({ ...prev, ...newWidths }))
    }
  }, [columnResizing, hasInitialWidths])

  const lastPushedSort = React.useRef<string | undefined>(metaSort)

  React.useEffect(() => {
    if (!sortValue) return
    if (sortValue === lastPushedSort.current) return
    lastPushedSort.current = sortValue
    onMetaChange('sort', sortValue)
  }, [sortValue, onMetaChange])

  React.useLayoutEffect(() => {
    const updateHeaderHeight = () => {
      setHeaderHeight(headerRef.current?.offsetHeight || 0)
    }

    if (!hasInitialWidths) {
      return undefined
    }

    updateHeaderHeight()
    window.addEventListener('resize', updateHeaderHeight)

    return () => window.removeEventListener('resize', updateHeaderHeight)
  }, [headerGroups, columnWidths, hasInitialWidths])

  React.useLayoutEffect(() => {
    if (hasInitialWidths || loading) return

    const nextWidths = baseColumns.reduce(
      (acc, column) => {
        if (column.width != null) {
          acc[column.id] = Number(column.width)
          return acc
        }

        const cell = measurementHeaderRefs.current[column.id]
        if (cell) {
          acc[column.id] = Math.max(Math.ceil(cell.getBoundingClientRect().width), column.minWidth || 0)
        }
        return acc
      },
      {} as Record<string, number>
    )

    if (columnIds.length > 0 && columnIds.every(id => nextWidths[id] != null)) {
      setColumnWidths(nextWidths)
    }
  }, [baseColumns, columnIds, hasInitialWidths, loading])

  const getColumnStyles = (column: any) => {
    const styles: React.CSSProperties = {}
    if (column.width) {
      styles.width = column.width
      styles.maxWidth = column.width
      styles.minWidth = column.width
    } else {
      if (column.minWidth) styles.minWidth = column.minWidth
      if (column.maxWidth) styles.maxWidth = column.maxWidth
    }
    return styles
  }

  const renderColumnHeader = (column: any) => {
    const view = column.render('Header')
    if (column.id === 'thumb' && !view) {
      return <span style={{ visibility: 'hidden' }}>Thumbnail</span>
    }

    return view
  }

  const getColumnWidth = (column: any) => {
    if (column.totalWidth) return column.totalWidth
    if (column.width) return column.width
    if (column.minWidth) return column.minWidth
    return 0
  }

  const resizeHandles = React.useMemo(() => {
    if (!hasInitialWidths) return []

    const headerGroup = headerGroups[0]
    if (!headerGroup) return []

    let offset = SELECTION_COLUMN_WIDTH

    return headerGroup.headers.reduce((handles: any[], column: any) => {
      offset += getColumnWidth(column)

      if (!column.canResize) return handles

      handles.push({
        id: column.id,
        left: offset,
        isResizing: column.isResizing,
        resizerProps: column.getResizerProps()
      })

      return handles
    }, [])
  }, [headerGroups, hasInitialWidths])

  const renderHeaderRow = (options: { resizable: boolean; attachMeasurementRefs: boolean }) =>
    headerGroups.map(headerGroup => (
      <tr {...headerGroup.getHeaderGroupProps()} className="rt-tr">
        <th className="rt-th" style={SELECTION_COLUMN_STYLE}>
          <Checkbox checked={selectAll} onChange={toggleAll} />
        </th>
        {headerGroup.headers.map(column => {
          const headerProps = options.resizable
            ? column.getHeaderProps()
            : column.getHeaderProps((column as any).getSortByToggleProps())

          return (
            <th
              {...headerProps}
              ref={
                options.attachMeasurementRefs
                  ? cell => {
                      measurementHeaderRefs.current[column.id] = cell
                    }
                  : undefined
              }
              className={[
                'rt-th',
                (column as any).isSorted && ((column as any).isSortedDesc ? '-sort-desc' : '-sort-asc'),
                (column as any).canSort && '-cursor-pointer'
              ]
                .filter(Boolean)
                .join(' ')}
              style={options.resizable ? getColumnStyles(column) : undefined}
            >
              {options.resizable ? (
                <div className="listing-manager-header-cell-content">
                  <div
                    {...(column as any).getSortByToggleProps()}
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {renderColumnHeader(column)}
                  </div>
                </div>
              ) : (
                renderColumnHeader(column)
              )}
            </th>
          )
        })}
      </tr>
    ))

  if (loading) {
    return <Spinner />
  }

  return (
    <>
      <div className="listing-manager-table-scroll">
        <div className="ReactTable -striped listing-manager-resizable-table">
          {hasInitialWidths && headerHeight > 0 ? (
            <div className="listing-manager-header-resize-overlay" style={{ height: headerHeight }}>
              {resizeHandles.map(handle => (
                <div
                  key={handle.id}
                  {...handle.resizerProps}
                  onClick={e => e.stopPropagation()}
                  className={`resizer ${handle.isResizing ? 'isResizing' : ''}`}
                  style={{ left: handle.left }}
                />
              ))}
            </div>
          ) : null}
          <table {...getTableProps()} className="rt-table">
            <thead ref={headerRef} className="rt-thead -header">
              {renderHeaderRow({ resizable: hasInitialWidths, attachMeasurementRefs: !hasInitialWidths })}
            </thead>

            <thead className="rt-thead -filters">
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()} className="rt-tr">
                  <th className="rt-th" style={SELECTION_COLUMN_STYLE} />
                  {headerGroup.headers.map(column => (
                    <th
                      key={column.id}
                      className="rt-th"
                      style={{
                        ...(hasInitialWidths ? getColumnStyles(column) : {}),
                        verticalAlign: 'middle',
                        padding: '4px 8px'
                      }}
                    >
                      {(column as any).filterable && (column as any).Filter ? (column as any).render('Filter') : null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()} className="rt-tbody">
              {rows.map((row, rowIndex) => {
                prepareRow(row)
                return (
                  <tr {...row.getRowProps()} className={`rt-tr ${rowIndex % 2 === 0 ? '-odd' : '-even'}`}>
                    <td className="rt-td" style={SELECTION_COLUMN_STYLE}>
                      <Checkbox
                        checked={isSelected(row.original._id)}
                        onChange={() => toggleSelection(row.original._id)}
                      />
                    </td>
                    {row.cells.map(cell => (
                      <td
                        {...cell.getCellProps()}
                        className="rt-td"
                        style={hasInitialWidths ? getColumnStyles(cell.column) : undefined}
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="listing-manager-table-pagination">
        <Button
          large
          style={style.pagination.button}
          onClick={() => onMetaChange('cursor', meta?.previousCursor)}
          disabled={!meta?.previousCursor}
          loading={!!loading}
        >
          Previous
        </Button>{' '}
        <FormGroup inline label="Page size" style={style.pagination.pageSize}>
          <HTMLSelect
            options={[{ value: 10 }, { value: 15 }, { value: 30 }, { value: 50 }, { value: 100 }]}
            value={meta?.perPage || 10}
            onChange={e => onMetaChange('perPage', Number(e.target.value))}
            disabled={loading}
          />
        </FormGroup>
        <Button
          large
          style={style.pagination.button}
          onClick={() => onMetaChange('cursor', meta?.nextCursor)}
          disabled={!meta?.nextCursor}
          loading={!!loading}
        >
          Next
        </Button>
      </div>
    </>
  )
}

export { CursorPaginatedTable }
export default React.memo(CursorPaginatedTable)
